import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { SignupFlowService, SignupStep } from '../services/signup-flow.service';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SignupFlowGuard implements CanActivate {
  constructor(
    private signupFlowService: SignupFlowService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Check if user is already authenticated
    const isAuthenticated = this.authService.isAuthenticatedSync();
    if (isAuthenticated) {
      // Already logged in, redirect to home
      return this.router.createUrlTree(['/home']);
    }

    // Get the required step from route data
    const requiredStep = route.data['requiredStep'] as SignupStep;

    // If no required step specified, allow access (for the main signup route)
    if (!requiredStep) {
      return true;
    }

    const state = this.signupFlowService.getState();

    // For the email step (first step), always allow access
    if (requiredStep === 'email') {
      return true;
    }

    // Stripe returns users to the welcome route after hosted Checkout. Browser
    // session storage can be unavailable after that round-trip, so allow the
    // page to recover the signup from the Checkout session server-side.
    if (requiredStep === 'welcome' && route.queryParamMap.has('session_id')) {
      return true;
    }

    // Check if user can access this step
    if (!this.signupFlowService.canAccessStep(requiredStep)) {
      // Redirect to appropriate step based on current state
      return this.redirectToAppropriateStep(state.currentStep);
    }

    // Check if user has required data for this step
    if (!this.signupFlowService.hasRequiredDataForStep(requiredStep)) {
      // Find the step where data is missing
      const missingDataStep = this.findStepWithMissingData(requiredStep);
      return this.redirectToAppropriateStep(missingDataStep);
    }

    // All checks passed
    return true;
  }

  /**
   * Find the earliest step that has missing required data
   */
  private findStepWithMissingData(targetStep: SignupStep): SignupStep {
    const steps: SignupStep[] = ['email', 'verify', 'country', 'plan', 'business', 'payment', 'welcome'];

    for (const step of steps) {
      if (step === targetStep) {
        break;
      }

      if (!this.signupFlowService.hasRequiredDataForStep(step)) {
        return step;
      }
    }

    return 'email';
  }

  /**
   * Create a URL tree for the appropriate signup step
   */
  private redirectToAppropriateStep(step: SignupStep): UrlTree {
    switch (step) {
      case 'email':
        return this.router.createUrlTree(['/signup']);
      case 'verify':
        return this.router.createUrlTree(['/signup/verify']);
      case 'country':
        return this.router.createUrlTree(['/signup/country']);
      case 'plan':
        return this.router.createUrlTree(['/signup/plan']);
      case 'business':
        return this.router.createUrlTree(['/signup/business']);
      case 'payment':
        return this.router.createUrlTree(['/signup/payment']);
      case 'welcome':
        return this.router.createUrlTree(['/signup/welcome']);
      default:
        return this.router.createUrlTree(['/signup']);
    }
  }
}
